// Phase 1A Step 34 — calculator math + count-up hook.
//
// Verbatim port from handoff/landing-reference/calculator.jsx. The hero
// math is locked per handoff README "must not change":
//   default GH₵85,598 protected pocket on a GH₵300 / 3.0× / 10-week chain
//   at 60% takeout (chain math result of 50% takeout per handoff defaults).
//
// Per-row math:
//   win        = stake * odds
//   takeoutAmt = win * takeout
//   nextStake  = win - takeoutAmt = win * (1 - takeout)
//   pocketIfStop[i] = sum(prior takeouts) + win[i]

import { useEffect, useRef, useState } from "react";

export interface ChainRow {
  week: number;
  stake: number;
  win: number;
  kept: number;
  next: number;
  cumKept: number;
  pocketIfStop: number;
}

export function buildChain(
  stake: number,
  weeks: number,
  odds: number,
  takeout: number,
): ChainRow[] {
  const rows: ChainRow[] = [];
  let s = stake;
  let cumKept = 0;
  for (let i = 1; i <= weeks; i++) {
    const win = s * odds;
    const kept = win * takeout;
    const next = win - kept;
    cumKept += kept;
    rows.push({
      week: i,
      stake: s,
      win,
      kept,
      next,
      cumKept,
      pocketIfStop: 0, // populated in second pass below
    });
    s = next;
  }
  // pocketIfStop = (cumulative kept from weeks 1..N-1) + (week N's win)
  let runningKept = 0;
  for (let i = 0; i < rows.length; i++) {
    rows[i]!.pocketIfStop = runningKept + rows[i]!.win;
    runningKept = rows[i]!.cumKept;
  }
  return rows;
}

export function fmtBig(n: number): string {
  if (!isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
}

export function compact(n: number): string {
  if (!isFinite(n)) return "—";
  if (n < 1000) return Math.round(n).toLocaleString();
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10000 ? 1 : 0) + "k";
  if (n < 1_000_000_000) return (n / 1_000_000).toFixed(n < 10_000_000 ? 2 : 1) + "M";
  return (n / 1_000_000_000).toFixed(2) + "B";
}

// Smooth count-up animation hook. Verbatim port — easeOut cubic, requestAF.
export function useCountUp(target: number, dur = 700): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number>(performance.now());
  const rafRef = useRef<number | null>(null);
  const valRef = useRef(target);
  valRef.current = val;
  useEffect(() => {
    fromRef.current = valRef.current;
    startRef.current = performance.now();
    const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);
    const tick = (now: number): void => {
      const t = Math.min(1, (now - startRef.current) / dur);
      const next = fromRef.current + (target - fromRef.current) * easeOut(t);
      setVal(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, dur]);
  return val;
}
